<script setup lang="ts">
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import {ref, computed, onMounted, onUnmounted} from 'vue';
import type {ConsentApprovalRequest} from '@/ts/types/dto/user-dto';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';

const emit = defineEmits<{
  accepted: [];
  close: [];
}>();

const api = useApi();
const logger = useLogger('MedicalDeviceConsentModal');
const $q = useQuasar();
const {t} = useI18n();
const showModal = defineModel<boolean>({required: true});

const isProcessing = ref(false);
const hasScrolledToBottom = ref(false);
const isCheckboxChecked = ref(false);
const scrollContainerRef = ref<HTMLElement | null>(null);

const canSubmit = computed(() => hasScrolledToBottom.value && isCheckboxChecked.value && !isProcessing.value);

function handleScroll(event: Event): void {
  const target = event.target as HTMLElement;
  if (!target) {return;}

  const scrollPosition = target.scrollTop + target.clientHeight;
  const {scrollHeight} = target;
  // Consider scrolled to bottom if within 10px of the bottom
  const threshold = 10;

  if (scrollPosition >= scrollHeight - threshold) {
    hasScrolledToBottom.value = true;
  }
}

function checkInitialScrollState(): void {
  if (scrollContainerRef.value) {
    const container = scrollContainerRef.value;
    // If content doesn't overflow, user has effectively seen all content
    if (container.scrollHeight <= container.clientHeight) {
      hasScrolledToBottom.value = true;
    }
  }
}

onMounted(() => {
  // Check if content needs scrolling after mount
  setTimeout(checkInitialScrollState, 100);
});

onUnmounted(() => {
  // Reset state when modal is unmounted
  hasScrolledToBottom.value = false;
  isCheckboxChecked.value = false;
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) {return;}

  isProcessing.value = true;

  try {
    const request: ConsentApprovalRequest = {
      consentType: 'medical_device',
      approved: true,
    };

    await api.me.putConsentApproval(request);

    showModal.value = false;
    emit('accepted');
  } catch (error) {
    logger.error('Failed to submit medical device consent:', error)();
    $q.notify({
      type: 'negative',
      message: t('medical_device_consent_error'),
    });
  } finally {
    isProcessing.value = false;
  }
}

function handleDismiss(): void {
  showModal.value = false;
  emit('close');
}
</script>

<template>
  <balm-dialog
    v-model="showModal"
    theme="neutral-0"
    type="fit-content"
    :no-x="true"
    :no-scroll="true"
    persistent
    role="dialog"
    :aria-labelledby="'medical-device-consent-title'"
    aria-modal="true"
  >
    <div class="medical-device-consent-modal">
      <div class="modal-header">
        <h2 id="medical-device-consent-title" class="modal-title">
          {{ $t('medical_device_consent_title') }}
        </h2>
        <flat-icon-btn
          class="dismiss-btn"
          icon="x-close.svg"
          :label="$t('aria_close_modal')"
          @click="handleDismiss"
        />
      </div>

      <div class="modal-content">
        <div
          ref="scrollContainerRef"
          class="scroll-container"
          tabindex="0"
          @scroll="handleScroll"
        >
          <div class="consent-body">
            <p>{{ $t('medical_device_consent_body_1') }}</p>
            <p>{{ $t('medical_device_consent_body_2') }}</p>
          </div>

          <div :class="['scroll-hint', {'scroll-hint--hidden': hasScrolledToBottom}]">
            <span class="scroll-hint-text">{{ $t('medical_device_consent_scroll_hint') }}</span>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span class="scroll-hint-arrow" aria-hidden="true">↓</span>
          </div>
        </div>

        <div class="checkbox-container">
          <label class="checkbox-label">
            <input
              v-model="isCheckboxChecked"
              type="checkbox"
              class="checkbox-input"
              :disabled="!hasScrolledToBottom"
            />
            <span :class="['checkbox-text', {'checkbox-text--disabled': !hasScrolledToBottom}]">
              {{ $t('medical_device_consent_acknowledgment') }}
            </span>
          </label>
        </div>

        <div class="buttons">
          <balm-btn
            :label="$t('accept')"
            theme="blue"
            paddings="p2-5"
            :loading="isProcessing"
            :disable="!canSubmit"
            :aria-describedby="'medical-device-consent-title'"
            @click="handleSubmit"
          />
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.medical-device-consent-modal {
  padding: var(--Spacing-spacing-4);
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  @include screen-lg {
    min-width: 500px;
    max-width: 700px;
  }
}

.modal-header {
  text-align: center;
  flex-shrink: 0;
  position: relative;
  padding-right: var(--Spacing-spacing-8); // Make room for dismiss button
}

.dismiss-btn {
  position: absolute;
  top: calc(-1 * var(--Spacing-spacing-2));
  right: calc(-1 * var(--Spacing-spacing-2));
}

.modal-title {
  @include font-h2-bold;
  margin-bottom: var(--Spacing-spacing-4);
}

.modal-content {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.scroll-container {
  overflow-y: auto;
  scrollbar-gutter: stable;
  max-height: 50vh;
  position: relative;

  @include screen-lg {
    max-height: 400px;
  }
}

.consent-body {
  @include font-body;

  p {
    margin-bottom: var(--Spacing-spacing-4);

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.scroll-hint {
  position: sticky;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--Neutral-UI-neutral-ui-0) 80%, transparent) 0%,
    color-mix(in srgb, var(--Neutral-UI-neutral-ui-0) 100%, transparent) 100%
  );
  padding: var(--Spacing-spacing-3) 0 var(--Spacing-spacing-1);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-1);

  &--hidden {
    display: none;
  }
}

.scroll-hint-text {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.scroll-hint-arrow {
  font-size: 1.5rem;
  color: var(--Neutral-UI-neutral-ui-60);
  animation: bounce 1.5s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(5px);
  }
}

.checkbox-container {
  margin-top: var(--Spacing-spacing-4);
  padding-top: var(--Spacing-spacing-4);
  border-top: 1px solid var(--Neutral-UI-neutral-ui-20);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  cursor: pointer;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.checkbox-text {
  @include font-body;

  &--disabled {
    color: var(--Neutral-UI-neutral-ui-60);
  }
}

.buttons {
  display: flex;
  justify-content: center;
  margin-top: var(--Spacing-spacing-6);
}
</style>
